import React, { useEffect, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import styled from 'styled-components';
import { Text } from 'components';
import { tablet, useQuery } from 'styles/breakpoints';
import quizSvgs from 'utils/constants/quizSvgs';

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 0;
  max-width: 28.75rem;
  width: 100%;
  position: relative;
  z-index: 3;
  @media ${tablet} {
    max-width: unset;
    padding: 2.25rem 1rem;
  }
`;

const ImageWrapper = styled.div`
  width: 15rem;
  height: 15rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  @media ${tablet} {
    width: 9.75rem;
    height: 9.75rem;
  }
`;

const Title = styled(Text)`
  color: #fff;
  text-align: center;
  font-size: 1.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: 2.0625rem;
  margin-bottom: 2rem;
  @media ${tablet} {
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
    line-height: 2rem;
  }
`;

const StyledPercent = styled(Text)`
  position: absolute;
  color: #f7c800;
  text-align: center;
  font-size: 3.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.03575rem;

  @media ${tablet} {
    font-size: 2.25rem;
    line-height: 150%;
    letter-spacing: -0.02475rem;
  }
`;

const Container = styled.div`
  display: flex;
  padding-top: 0.75rem;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  border-top: 0.4px solid #d9dce0;
  max-width: 15rem;
  width: 100%;
  margin-top: 1.5rem;
  @media ${tablet} {
    max-width: 11.5rem;
  }
`;

const BottomLabel = styled(Text)`
  color: #f7c800;
  font-size: 1.75rem;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: -0.042rem;
  @media ${tablet} {
    font-size: 1.375rem;
    letter-spacing: -0.033rem;
    font-weight: 600;
  }
`;

const BottomSubtitle = styled(Text)`
  color: #fff;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 300;
  line-height: 140%;
  letter-spacing: -0.021rem;
  @media ${tablet} {
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: -0.018rem;
  }
`;

const Review = styled.div`
  display: flex;
  padding: 1.5rem 0.75rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  align-self: stretch;
  border-radius: 0.75rem;
  background: #f0f3fb;
  margin-top: 1.5rem;
  @media ${tablet} {
    padding: 0.875rem 0.75rem;
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
`;

const ReviewTitle = styled(Text)`
  color: #000;
  text-align: center;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.011rem;
  @media ${tablet} {
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: -0.00963rem;
  }
`;

const ReviewText = styled(Text)`
  color: #28293d;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 1.4rem */
  letter-spacing: -0.024rem;
  @media ${tablet} {
    font-size: 0.75rem;
    line-height: 1.125rem;
    letter-spacing: -0.018rem;
  }
`;

const LOADING_DURATION = 7500; // Total loading duration in milliseconds
const INTERVAL = 75; // Interval for updating the progress (60ms for smoother updates)

interface CircularProgressBarProps {
  onComplete: () => void;
  title?: string;
  bottomLabel: string;
  bottomSubtitle: string;
  reviewTitle: string;
  reviewName: string;
  review: string;
}

const CircularProgressBarReview = ({
  onComplete,
  title,
  bottomLabel,
  bottomSubtitle,
  reviewTitle,
  reviewName,
  review,
}: CircularProgressBarProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const { isTablet } = useQuery();
  const renderSvgImage = (svgKey: string) => {
    const Svg = quizSvgs[svgKey];
    return <Svg />;
  };

  useEffect(() => {
    const totalTicks = LOADING_DURATION / INTERVAL;
    let tickCount = 0;

    const animationInterval = setInterval(() => {
      tickCount += 1;
      const newProgress = Math.min((tickCount / totalTicks) * 100, 100);
      setProgress(newProgress);

      if (tickCount >= totalTicks) {
        clearInterval(animationInterval);
        setIsLoading(false);
      }
    }, INTERVAL);

    return () => {
      clearInterval(animationInterval);
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 1000);

      return () => clearTimeout(completeTimer);
    }
  }, [isLoading, onComplete]);

  return (
    <LoaderContainer>
      <Title textAlign="center">{title}</Title>
      <div>
        <ImageWrapper>
          <CircularProgressbar
            value={progress}
            text=""
            styles={buildStyles({
              pathTransitionDuration: 0.25,
              pathColor: '#F7C800',
              trailColor: '#F0F3FB99',
              strokeLinecap: 'round',
            })}
          />
          <StyledPercent>{Math.round(progress)}%</StyledPercent>
        </ImageWrapper>
      </div>
      <Container>
        <BottomLabel>{bottomLabel}</BottomLabel>
        <BottomSubtitle>{bottomSubtitle}</BottomSubtitle>
      </Container>
      <Review>
        {renderSvgImage('reviewStars')}
        <TitleContainer>
          <ReviewTitle>{reviewTitle}</ReviewTitle>
          <ReviewTitle>{reviewName}</ReviewTitle>
        </TitleContainer>
        <ReviewText>{review}</ReviewText>
      </Review>
    </LoaderContainer>
  );
};

export default CircularProgressBarReview;
